import Vue from 'vue'
import main from '@/main'
import {forEach, getFormatDate} from '@/libs/tools.js'
import {getVcsMembers} from '@/api/conferencejs.js'
import Participant from './participant.js'

class Room {
  meetingParticipants = []
  ringParticipants = new Set()
  fixParticipants = {}
  tempParticipants = {}
  inviteParticipants = []
  name = ''
  no=''
  number = null
  route=null
  host=null
  intercom=null
  isVideo=false
  record = false
  forceRecord = false
  isTemp = false
  deptId = ''
  mute = false
  meeting = false
  sharing = false
  changePanel = false

  meetingInfo = []
  reloadTimeout = null
  meetingNum = 0

  constructor(name,meetingNo,meetingNumber,route,host,intercom,isVideo,record,deptId,isTemp) {
    this.name = name
    this.isTemp = isTemp
    this.deptId = deptId
    this.no = meetingNo
    this.number = meetingNumber
    this.route = route
    this.host = host
    this.intercom = intercom
    this.isVideo = isVideo
    this.record = record
    if (isTemp===undefined||isTemp===null) {
      this.isTemp = name.indexOf('-a-')>-1
    } else {
      this.isTemp = isTemp
    }
    if (this.isTemp) {
      this.no = this.name
      //this.number = this.name
    }
    this.meetingInfo = []
  }

  participantKey(participant) {
    if ((participant.type == 1 || participant.type == 2) && participant.number) {
      return participant.number
    }
    return participant.name
  }

  /**
   * Resolve participant by signaling name / number / identity
   */
  resolveParticipant(name) {
    if (!name && name !== 0) return null
    let op = this.fixParticipants[name] || this.tempParticipants[name]
    if (op) return op
    op = this.getParticipantByNumber(name)
    if (op) return op
    const all = Object.assign({}, this.fixParticipants, this.tempParticipants)
    for (const key in all) {
      const p = all[key]
      if (p && (p.name === name || p.identity === name || String(p.number) === String(name))) {
        return p
      }
    }
    return null
  }

  ensureIceQueue(name) {
    if (!this._iceQueues) {
      this._iceQueues = new Map()
    }
    const key = String(name)
    let queue = this._iceQueues.get(key)
    if (!queue) {
      queue = []
      this._iceQueues.set(key, queue)
    }
    return queue
  }

  enqueueIceCandidate(name, candidate) {
    if (candidate == null || name == null || name === '') return
    this.ensureIceQueue(name).push(candidate)
    this.drainIceCandidates(name)
  }

  /**
   * 按 name 把全局队列交给已存在的 rtcPeer。
   * kurento 在非 stable 时会把 candidate 放进内部缓冲，callback 要等 signalingstatechange 才回来，
   * 因此不能用该 callback 当作“本批结束并 delete”。
   * 同步处理完后如果队列里又有数据，继续 drain；没有 peer 时原样保留。
   */
  drainIceCandidates(name) {
    if (!this._iceQueues || name == null || name === '') return
    const key = String(name)
    const queue = this._iceQueues.get(key)
    if (!queue || queue.length === 0) return

    if (!this._iceDrainLocks) {
      this._iceDrainLocks = new Set()
    }
    if (this._iceDrainLocks.has(key)) return

    const op = this.resolveParticipant(name)
    if (!op || !op.rtcPeer) {
      return
    }

    this._iceDrainLocks.add(key)
    try {
      while (queue.length) {
        const current = this.resolveParticipant(name)
        if (!current || !current.rtcPeer) {
          break
        }
        const candidate = queue.shift()
        try {
          current.rtcPeer.addIceCandidate(candidate, (error) => {
            if (error) {
              console.warn(`addIceCandidate failed for ${key}:`, error)
            }
          })
        } catch (e) {
          queue.unshift(candidate)
          console.warn(`addIceCandidate exception for ${key}:`, e)
          break
        }
      }
    } finally {
      this._iceDrainLocks.delete(key)
      if (queue.length > 0) {
        const current = this.resolveParticipant(name)
        if (current && current.rtcPeer) {
          this.drainIceCandidates(key)
        }
      } else {
        this._iceQueues.delete(key)
      }
    }
  }

  participantIceKeys(participant) {
    if (!participant) return []
    const keys = [participant.name, participant.number, participant.identity, this.participantKey(participant)]
    const seen = new Set()
    const result = []
    keys.forEach(k => {
      if (k == null || k === '') return
      const key = String(k)
      if (seen.has(key)) return
      seen.add(key)
      result.push(key)
    })
    return result
  }

  /**
   * 把该成员名下尚未发出的 candidate flush 到 peer。
   * peer 不存在时不删除队列。
   */
  flushIceToPeer(participant) {
    if (!participant) return
    if (this._earlyIceCandidates) {
      this.participantIceKeys(participant).forEach(key => {
        const queued = this._earlyIceCandidates.get(key)
        if (!queued || queued.length === 0) return
        const pending = queued.splice(0, queued.length)
        pending.forEach(c => this.ensureIceQueue(key).push(c))
        this._earlyIceCandidates.delete(key)
      })
    }
    if (participant._pendingCandidates && participant._pendingCandidates.length) {
      const pending = participant._pendingCandidates.splice(0, participant._pendingCandidates.length)
      const key = participant.name != null && participant.name !== '' ? participant.name : this.participantKey(participant)
      pending.forEach(c => this.ensureIceQueue(key).push(c))
    }
    this.participantIceKeys(participant).forEach(key => this.drainIceCandidates(key))
  }

  addParticipant(participant,inRoom) {
    this.meetingNum=0
    let key = this.participantKey(participant)
    // 已存在则原地合并，避免替换对象导致 Member 重建 / 黑屏
    if (participant.isTemp) {
      let op = this.tempParticipants[key]
      if (op) {
        op.mergeFrom(participant)
        participant = op
      } else {
        Vue.set(this.tempParticipants, key, participant)
      }
    } else {
      let op = this.fixParticipants[key]
      if (op) {
        // 保留媒体相关状态，合并业务字段
        const keepStatus = op.status === 'Talking' && inRoom
        op.mergeFrom(participant)
        if (keepStatus) {
          op.setStatus('Talking')
        }
        participant = op
      } else {
        // 临时成员升级为固定成员时迁移 rtcPeer，candidate 只搬不丢
        let tp = this.tempParticipants[key]
        if (tp) {
          if (tp.rtcPeer) {
            participant.rtcPeer = tp.rtcPeer
            participant.speakHark = tp.speakHark
            participant.playing = tp.playing
            participant._remoteDescSet = !!tp._remoteDescSet
            participant._disposed = tp._disposed
            participant._iceCheckingSince = tp._iceCheckingSince || 0
          }
          if (tp._pendingCandidates && tp._pendingCandidates.length) {
            participant._pendingCandidates = (participant._pendingCandidates || []).concat(tp._pendingCandidates)
            tp._pendingCandidates = []
          }
          Vue.delete(this.tempParticipants, key)
        }
        Vue.set(this.fixParticipants, key, participant)
      }
    }

    this.flushIceToPeer(participant)

    if (inRoom && (participant.status === 'Talking' || participant.status === 'Ringing')) {
      this.meeting = true
      let listChanged = false
      if (participant.status === 'Talking') {
        if (this.meetingParticipants.indexOf(key) < 0) {
          this.meetingParticipants.push(key)
          listChanged = true
        }
        this.removeInviteParticipant('name', key)
        this.ringParticipants.delete(key)
      }else if (!this.ringParticipants.has(key)){
        this.ringParticipants.add(key)
        listChanged = true
      }
      if (listChanged) {
        main.$root.bus.$emit('meetingChange',this.meetingParticipants)
      }
    }
  }

  addMeetingNum() {
    this.meetingNum++;
  }

  changeVcsMember(json){
    let type = json.type
    let name = json.name
    let muted = json.mute
    let muteVideo = json.muteVideo
    let inviteName = name
    let shareName = json.shareName
    //先修改已有成员（原地更新，不重建对象）
    let op = this.resolveParticipant(name)
    if (op) {
      op.muted = muted
      op.muteVideo = muteVideo
      op.setStatus('Talking')
      this.meeting = true
      if (op.type == '4' || op.type == '5') {
        op.muted = true
        muted = true
      }
      if (op.muted) {
        op.meetingIcon = 'conf_mute'
      } else {
        op.meetingIcon = 'conf'
      }
      const meetKey = this.participantKey(op)
      if (this.meetingParticipants.indexOf(meetKey) < 0) {
        this.meetingParticipants.push(meetKey)
        main.$root.bus.$emit('meetingChange',this.meetingParticipants)
      }
      inviteName = op.name
      this.removeInviteParticipant('name', op.name)
    }else {
      //需要重新生成成员
      let arr = []
      if (type === 'SIP') {
        type = 'sip'
      }else if (type==='CAMERA'){
        type='camera'
        muted = true
      }else if (type == 'SCREEN') {
        //是屏幕共享直接添加一个成员即可
        let participant = new Participant(name, this.name, '', 7, true, '');
        participant.isShare = true
        participant.isShowShare = shareName != main.$store.state.user.userName
        participant.isTemp = true
        participant.muteVideo = false
        participant.meetingIcon = 'conf'
        participant.muted = true
        participant.setStatus('Talking')
        participant.setDept('','all_area')
        //对Icon进行处理
        participant.icon = ''
        participant.net = ''
        participant.gradeName = ''
        participant.realName = shareName
        this.addParticipant(participant, true);
        this.sharing = true
        this.changePanel = true
        if (this.meetingParticipants.indexOf(name) < 0) {
          this.meetingParticipants.push(name)
          main.$root.bus.$emit('meetingChange',this.meetingParticipants)
        }
        return
      }
      let p = {name: name, type: type, mute: muted,muteVideo:muteVideo};
      arr.push(p)
      getVcsMembers(arr).then(res=>{
        let response = res.data
        if (response.code === 200) {
          let ps = response.data
          forEach(ps,d=>{
            let name1 = d.name
            if (d.channel) {
              name1 = d.name + '_' + d.channel
              //gb28181 移除
              this.removeInviteParticipant('name', d.name)
            }
            let participant = new Participant(name1, this.name, d.exten, d.type, d.vcs, d.gradeNum)
            if (d.channel) {
              participant.realName = d.name
            }
            if (!participant.number) {
              participant.number = participant.name
            }
            participant.isTemp = d.temp;
            participant.muteVideo = muteVideo
            if (d.inRoom) {
              if (d.mute) {
                participant.muted = d.mute
                participant.meetingIcon = 'conf_mute'
              } else {
                participant.meetingIcon = 'conf'
              }
            } else {
              participant.meetingIcon = ''
            }
            if (d.status === 'UnKnown') {
              d.status = 'UnKnow'
            }
            participant.setStatus(d.status)
            participant.setDept(d.deptId,d.deptName)
            //对Icon进行处理
            participant.icon = ''
            participant.net = d.net
            participant.gradeName = d.gradeName
            inviteName = d.name
            this.addParticipant(participant,d.inRoom)
          })
        }
      })

    }
    if (this.meetingParticipants.indexOf(name) < 0) {
      this.meetingParticipants.push(name)
      main.$root.bus.$emit('meetingChange',this.meetingParticipants)
    }
    //处理掉邀请
    this.removeInviteParticipant('name',inviteName)
  }

  vcsMemberLeave(json) {
    let type = json.type
    let name = json.name
    //删除开会中
    if (this.meetingParticipants.indexOf(name) > -1) {
      this.meetingParticipants.splice(this.meetingParticipants.indexOf(name),1)
      this.ringParticipants.delete(name)
      main.$root.bus.$emit('meetingChange',this.meetingParticipants)
    }
    //如果是screen
    if (type == 'SCREEN') {
      let participant = this.tempParticipants[name]
      if (participant) {
        participant.dispose();
        Vue.delete(this.tempParticipants,name)
      }
      this.sharing = false
      this.changePanel = false
      return
    }
    //判断是否是自己离开会议室
    if (type =='PC' && name === main.$store.state.user.userName) {
      if (this.meetingParticipants.length > 0) {
        for (const key in this.tempParticipants) {
          let participant = this.tempParticipants[key]
          if (participant.rtcPeer) {
            participant.dispose()
          }
        }
        for (const key in this.fixParticipants) {
          let participant = this.fixParticipants[key]
          if (participant.rtcPeer) {
            participant.dispose()
          }
        }
      }
    }
    let op = this.fixParticipants[name]
    if (!op) {
      op = this.tempParticipants[name]
      if (!op) {
        console.log('can`t find vcs member:', name)
        return
      }
      op.dispose()
      Vue.delete(this.tempParticipants,name)
      return
    }
    if (op.status === 'Talking') {
      op.status = 'Idle'
      /*if (op.type === 1 || op.type === 2) {
        op.status = 'Idle'
      }else{
        op.status = 'UnKnow'
      }*/
      if (op.type == '4' && op.isRtsp) {
        op.status = 'UnKnow'
      }
    }
    op.meetingIcon = ''

    //写入到未加入
    let invite = {}
    invite.name = op.name
    invite.type = op.type
    invite.canInvite = true
    invite.number = op.number
    this.inviteParticipants.unshift(invite)
    if (this.isTemp) {
      if (Object.keys(this.tempParticipants).length <= 0) {
        main.$store.commit('removeRoom',this)
      }
    }
    op.dispose()
  }

  /**
   * ICE：全局按 name 排队，仅在 rtcPeer 存在时交给 addIceCandidate。
   * 不要求 _remoteDescSet；kurento 会在 answer 之前自己缓冲。
   */
  vcsCandidate(json) {
    if (!json || !json.candidate || json.name == null || json.name === '') return
    this.enqueueIceCandidate(json.name, json.candidate)
  }

  /**
   * SDP Answer：等待 peer + have-local-offer，成功后再标记 remoteDesc 并 flush ICE
   */
  vcsAnswer(json) {
    const name = json.name
    const MAX_RETRIES = 8
    const INITIAL_DELAY = 200

    // 同一成员并发 answer 只处理最新一次
    if (!this._answerTasks) {
      this._answerTasks = new Map()
    }
    const taskId = (this._answerTasks.get(name) || 0) + 1
    this._answerTasks.set(name, taskId)

    const scheduleRetry = (retryCount) => {
      if (retryCount >= MAX_RETRIES) {
        console.error(`Max retries (${MAX_RETRIES}) reached for processing answer for ${name}`)
        return
      }
      const delay = INITIAL_DELAY * Math.pow(1.5, retryCount)
      setTimeout(() => attemptProcessAnswer(retryCount + 1), delay)
    }

    const attemptProcessAnswer = (retryCount = 0) => {
      if (this._answerTasks.get(name) !== taskId) {
        return
      }

      const op = this.resolveParticipant(name)
      if (!op || !op.rtcPeer) {
        return scheduleRetry(retryCount)
      }

      const connection = op.rtcPeer.peerConnection
      if (!connection || connection.signalingState === 'closed') {
        return console.error('Cannot process answer: RTCPeerConnection is closed')
      }

      // 仅 have-local-offer 才能 processAnswer。stable 且已协商完则只补 flush。
      if (connection.signalingState !== 'have-local-offer') {
        if (connection.signalingState === 'stable' && op._remoteDescSet) {
          this.flushIceToPeer(op)
          return
        }
        return scheduleRetry(retryCount)
      }

      op.rtcPeer.processAnswer(json.sdpAnswer, (error) => {
        if (this._answerTasks.get(name) !== taskId) {
          return
        }
        const pc = op.rtcPeer && op.rtcPeer.peerConnection
        if (error) {
          console.error(`Error processing answer (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error)
          if (pc && pc.signalingState === 'have-local-offer' && retryCount < MAX_RETRIES) {
            scheduleRetry(retryCount)
          }
          return
        }
        if (!pc || pc.signalingState === 'closed') {
          return
        }
        op.markRemoteDescriptionSet()
        this.flushIceToPeer(op)
      })
    }

    attemptProcessAnswer()
  }

  clearParticipant() {
    if (!this.isVideo) {
      this.fixParticipants = {}
      this.tempParticipants = {}
      this.meetingParticipants = []
      this.ringParticipants.clear()
      this.meeting = false
    }else{
      if (!this.meeting) {
        this.fixParticipants = {}
        this.tempParticipants = {}
        this.meetingParticipants = []
        this.ringParticipants.clear()
      }
    }
  }

  getParticipants() {
    let all = []
    let gbName = new Set()
    for (const key in this.tempParticipants) {
      let participant = this.tempParticipants[key]
      if (participant.isShare && !participant.isShowShare) {
        continue;
      }
      if (participant.realName !== participant.name) {
        gbName.add(participant.realName);
      }
      all.push(this.tempParticipants[key])
    }
    let gbArr = Array.from(gbName)
    for (const key in this.fixParticipants) {
      let participant = this.fixParticipants[key]
      if (gbArr.length > 0) {
        if ((participant.type == '4' || participant.type == '5') && gbArr.indexOf(participant.name) > -1) {
          continue
        }
      }
      all.unshift(this.fixParticipants[key])
    }
    return all
  }

  getShareMember() {
    if (!this.sharing || this.tempParticipants.length === 0) {
      return null
    }
    for (const key in this.tempParticipants) {
      let participant = this.tempParticipants[key]
      if (participant.isShare) {
        return participant
      }
    }
    return null
  }

  getParticipant(name, type) {
    let fp = this.fixParticipants[name]
    let tp = this.tempParticipants[name]
    if (type === 'temp') {
      return tp
    }else if (type === 'fix') {
      return fp
    } else {
      return fp ? fp : tp
    }
  }

  getParticipantByNumber(number,appKey) {
    if (!number) {
      return null
    }
    for (const key in this.fixParticipants) {
      let participant = this.fixParticipants[key]
      /*if (participant.type !== 1) {
        continue
      }*/
      if (appKey) {
        if (appKey === participant.gradeNumber && participant.number === number) {
          return participant
        }
      }else {
        if (participant.number === number) {
          return participant
        }
      }
    }
    for (const key in this.tempParticipants) {
      let participant = this.tempParticipants[key]
      /*if (participant.type !== 1) {
        continue
      }*/
      if (appKey) {
        if (appKey === participant.gradeNumber && participant.number === number) {
          return participant
        }
      }else {
        if (participant.number === number) {
          return participant
        }
      }
    }
    return null
  }

  inFixReloadRoom(number, appKey, delay) {
    //直接修改状态就行
    let reload = false
    for (const key in this.fixParticipants) {
      let participant = this.fixParticipants[key]
      if (participant.number === number) {
        if (appKey) {
          if (appKey === participant.appKey) {
            reload = true
          }
        } else {
          reload = true
        }
      }
    }

    for (const key in this.tempParticipants) {
      let participant = this.tempParticipants[key]
      if (participant.number === number) {
        if (appKey) {
          if (appKey === participant.appKey) {
            reload = true
          }
        } else {
          reload = true
        }
      }
    }
    if (reload) {
      if (delay) {
        if (this.reloadTimer) {
          clearTimeout(this.reloadTimer)
        }
        this.reloadTimer = setTimeout(()=>{
          main.$root.bus.$emit('reloadConference', this.name)
          this.reloadTimer = null
        },delay)
      }else {
        main.$root.bus.$emit('reloadConference', this.name);
      }
    }
  }

  reloadRoom(delay) {
    if (!delay) {
      main.$root.bus.$emit('reloadConference', this.name)
    }else{
      if (this.reloadTimeout) {
        clearTimeout(this.reloadTimeout)
      }
      this.reloadTimeout = setTimeout(()=>{
        main.$root.bus.$emit('reloadConference', this.name)
      },delay)
    }
  }

  removeParticipant(key, value, appKey) {
    if (!key) {
      key = 'name'
    }
    let pt = null
    for (const pk in this.tempParticipants) {
      let participant = this.tempParticipants[pk]
      if (participant[key] === value) {
        if (appKey) {
          if (participant.appKey === appKey) {
            pt = participant
            break
          }
        } else {
          pt = participant
          break
        }

      }
    }
    if (!pt) {
      for (const pk in this.fixParticipants) {
        let participant = this.fixParticipants[pk]
        if (participant[key] === value) {
          if (appKey) {
            if (participant.appKey === appKey) {
              pt = participant
              break
            }
          } else {
            pt = participant
            break
          }
        }
      }
    }
    if (!pt) {
      if (this.meetingParticipants.length <= 0 && this.ringParticipants.size <= 0) {
        this.dispose()
      }
      return
    }
    let pKey = pt.type == 1 ? pt.number :pt.name
    if (this.meetingParticipants.indexOf(pKey) > -1) {
      this.meetingParticipants.splice(this.meetingParticipants.indexOf(pKey), 1)
      this.ringParticipants.delete(pKey)
      main.$root.bus.$emit('meetingChange',this.meetingParticipants)
    }
    if (pt.isTemp) {
      //临时移除
      this.ringParticipants.delete(pKey)
      Vue.delete(this.tempParticipants, pKey)
    } else {
      //固定的写入未加入中
      let json = {}
      json.name = pt.name
      json.type = pt.type
      json.canInvite = true
      json.number = pt.number
      this.inviteParticipants.unshift(json)
    }
    if (this.meetingParticipants.length <= 0 && this.ringParticipants.size <= 0) {
      this.dispose()
    }
  }

  //添加邀请（包含固定）成员
  addInviteParticipant(json) {
    if (this.inviteParticipants.length <= 0) {
      this.inviteParticipants.push(json)
      return
    }
    let participants = this.inviteParticipants.filter(i=>i.name === json.name)
    if (participants && participants.length > 0 || this.meetingParticipants.indexOf(json.name) > 0) {
      return
    }
    this.inviteParticipants.push(json)
  }

  addVcsMember(data) {
    if (data.roomName === this.name) {
      if (!this.forceRecord) {
        this.record = data.record
      }
      let participantNames = data.participantNames
      if (participantNames) {
        forEach(participantNames,pa=>{
          let name = pa.type == 1 ? pa.number : pa.name
          if (this.meetingParticipants.indexOf(name) < 0) {
            this.meetingParticipants.push(name)
            this.changeVcsMember(pa)
            main.$root.bus.$emit('meetingChange',this.meetingParticipants)
          }
        })
      }
    }
  }

  changeInviteState(key, appKey, value, delay) {
    if (!this.meeting) {
      return
    }
    if (!key) {
      key = 'name'
    }
    let reinvite = null
    for (let i = 0; i < this.inviteParticipants.length; i++) {
      let participant = this.inviteParticipants[i]
      if (participant[key] == value) {
        if (appKey) {
          if (appKey === participant.appKey) {
            reinvite = participant
            break
          }
        } else {
          reinvite = participant
          break
        }

      }
    }
    if (reinvite) {
      reinvite.canInvite = true
      if (this.reloadTimeout) {
        clearTimeout(this.reloadTimeout)
      }
      this.reloadTimeout = setTimeout(() => {
        main.$root.bus.$emit('reloadConference', this.name)
      }, delay)
    }
  }

  reloadInvite() {
    this.inviteParticipants = []
    if (this.meeting && Object.keys(this.fixParticipants).length > 0) {
      for (const key in this.fixParticipants) {
        let op = this.fixParticipants[key]
        let num = op.type == 1 ? op.number : op.name
        if (this.meetingParticipants.indexOf(num) < 0) {
          let invite = {}
          invite.name = op.name
          invite.type = op.type
          invite.canInvite = op.status == 'Idle'
          invite.number = op.number
          this.inviteParticipants.unshift(invite)
        }
      }
    }
    /*if (!this.meeting) {
      this.inviteParticipants = []
    }*/
  }

  info(key, value, appKey, type) {
    if (!key) {
      key = 'name'
    }
    let pt = null
    for (const pk in this.tempParticipants) {
      let participant = this.tempParticipants[pk]
      if (participant[key] === value) {
        if (appKey && participant.appKey === appKey) {
          pt = participant
          break
        } else {
          pt = participant
          break
        }

      }
    }
    if (!pt) {
      for (const pk in this.fixParticipants) {
        let participant = this.fixParticipants[pk]
        if (participant[key] === value) {
          if (appKey && participant.appKey === appKey) {
            pt = participant
            break
          } else {
            pt = participant
            break
          }

        }
      }
    }

    //生成消息
    let json = {}
    if (!pt) {
      json.name = value
    }else{
      json.name = pt.number
    }
    if (!json.name) {
      json.name = value
    }
    json.time = getFormatDate("HH:MM:SS", new Date())
    json.room = this.name
    json.type = type

    this.meetingInfo.unshift(json)
  }

  aysncInfo(name, type) {
    //生成消息
    let json = {}
    json.time = getFormatDate("HH:MM:SS", new Date())
    json.name = name
    json.room = this.name
    json.type = type
    json.showRoom = type != 'startShare' && type != 'endShare'
    this.meetingInfo.unshift(json)
  }

  removeInviteParticipant(key, value,appKey) {
    if (this.inviteParticipants.length < 0) {
      return
    }
    if (!key) {
      key = 'name'
    }
    let index = -1
    for (let i = 0; i < this.inviteParticipants.length; i++) {
      let ring = this.inviteParticipants[i]
      if (ring[key] === value) {
        if (appKey && ring.appKey === appKey) {
          index = i
        } else {
          index = i
        }

      }
    }
    if (index > -1) {
      this.inviteParticipants.splice(index, 1)
    }
  }

  meetings(status,screen=false) {
    let mm = []
    if (this.meetingParticipants.length < 0) {
      return mm
    }
    let control = main.$store.state.user.left
    forEach(this.meetingParticipants,m=>{
      let pt = null
      for (const key in this.fixParticipants) {
        let participant = this.fixParticipants[key]
        let pKey = participant.type == 1||participant.type == 2 ?participant.number : participant.name
        if (pKey === m) {
          pt=participant
          break
        }
      }
      if (!pt) {
        for (const key in this.tempParticipants) {
          let participant = this.tempParticipants[key]
          if (!screen && participant.isShare && !participant.isShowShare){
            continue
          }
          let pKey = participant.type == 1||participant.type == 2 ?participant.number : participant.name
          if (pKey === m) {
            pt=participant
            break
          }
        }
      }
      if (pt) {
        if (status) {
          if (pt.status === status) {
            if (pt.type == 1 && pt.number == control) {
              mm.unshift(pt)
            } else {
              mm.push(pt)
            }
          }
        } else {
          if (pt.type == 1 && pt.number == control) {
            mm.unshift(pt)
          } else {
            mm.push(pt)
          }
        }
      }
    })
    return mm
  }

  muteAll() {
    if (this.isVideo) {
      if (this.meetingParticipants.length > 0) {
        this.meetingParticipants.forEach(p=>{
          let participant = this.fixParticipants[p]
          participant = participant ? participant : this.tempParticipants[p]
          if (participant) {
            participant.setMute(true)
            this.aysncInfo(participant.name, 'mute')
          }
        })
      }
      /*for (const key in this.fixParticipants) {
        this.fixParticipants[key].setMute(true)
        this.aysncInfo(this.fixParticipants[key].name, 'mute')
      }
      for (const key in this.tempParticipants) {
        this.tempParticipants[key].setMute(true)
        this.aysncInfo(this.tempParticipants[key].name, 'mute')
      }*/
    }
  }

  unMuteAll() {
    if (this.isVideo) {
      if (this.meetingParticipants.length > 0) {
        this.meetingParticipants.forEach(p=>{
          let participant = this.fixParticipants[p]
          participant = participant ? participant : this.tempParticipants[p]
          if (participant) {
            participant.setMute(false)
            this.aysncInfo(participant.name, 'unMute')
          }
        })
      }
      /*for (const key in this.fixParticipants) {
        this.fixParticipants[key].setMute(false)
        this.aysncInfo(this.fixParticipants[key].name, 'unMute')
      }
      for (const key in this.tempParticipants) {
        this.tempParticipants[key].setMute(false)
        this.aysncInfo(this.tempParticipants[key].name, 'unMute')
      }*/
    }
  }

  inRoom(name) {
    return this.meetingParticipants.indexOf(name) > -1 || this.ringParticipants.has(name)
  }

  setForceRecord(forceRecord) {
    this.forceRecord = forceRecord
    /*if (forceRecord) {
      this.record = true
    }*/
  }

  dispose() {
    if (this.isVideo) {
      Object.values(this.tempParticipants).forEach(participant=>{
        participant.dispose()
      })
      Object.values(this.fixParticipants).forEach(participant=>{
        participant.dispose()
      })

      if (this._earlyIceCandidates) {
        this._earlyIceCandidates.clear()
      }
      if (this._iceQueues) {
        this._iceQueues.clear()
      }
      if (this._iceDrainLocks) {
        this._iceDrainLocks.clear()
      }
      if (this._answerTasks) {
        this._answerTasks.clear()
      }
      if (this._candidateManager) {
        this._candidateManager.clear()
      }
      /*for (const key in this.tempParticipants) {
        let participant = this.tempParticipants[key]
        if (participant.rtcPeer) {
          participant.dispose()
        }
      }
      for (const key in this.fixParticipants) {
        let participant = this.fixParticipants[key]
        if (participant.rtcPeer) {
          participant.dispose()
        }
      }*/
    }

    this.meeting = false
    this.record = this.forceRecord
    this.meetingParticipants = []
    this.meetingNum=0
    this.ringParticipants.clear()
    main.$root.bus.$emit('meetingChange',this.meetingParticipants)
    this.inviteParticipants = []
    this.tempParticipants = {}
    this.sharing = false
    if (this.isTemp) {
      main.$store.commit('removeRoom',this)
      main.$store.commit('setCurrRoom',null)
    }

  }
}
export default Room



// WEBPACK FOOTER //
// ./src/view/conference/room.js